/**
 * Prioris — Materials Approval submittal detail drawer.
 *
 * Slides in from the right when a submittal's Internal Ref is clicked.
 * Contains three tabs:
 *   1. Details  — all fields, status transitions, audit trail
 *   2. Comments — 3-party open/close thread (Contractor / Design Eng / Resident Eng)
 *   3. Mix Design (concrete_mix only) — full FHWA 1608 input form, saves to
 *      submittal.mix_design for reuse in QC Mix Design tab.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import {
  X, Loader2, MessageSquare, Beaker, ClipboardList, Send, CheckCircle2, Lock,
  ExternalLink, Trash2, User, Clock, Minus, Maximize2, Minimize2
} from 'lucide-react';
import SpecCatalogComplianceCard from './SpecCatalogComplianceCard';

const ROLES = ['Contractor', 'Design Engineer', 'Resident Engineer'];
const ROLE_COLOUR = {
  'Contractor':        { fg: '#60a5fa', bg: 'rgba(59,130,246,0.15)'  },
  'Design Engineer':   { fg: '#34d399', bg: 'rgba(16,185,129,0.15)'  },
  'Resident Engineer': { fg: '#fbbf24', bg: 'rgba(245,158,11,0.15)'  },
};

const STATUSES = ['Draft', 'Under Review', 'Action Required', 'Approved', 'Rejected', 'Superseded', 'Cancelled'];

// Panel size state persisted in localStorage
const MIN_W_VW = 40;
const MAX_W_VW = 95;
const DEFAULT_W_VW = 66;
function loadPrefs() {
  try {
    const raw = localStorage.getItem('ma-drawer-prefs');
    if (!raw) return { width_vw: DEFAULT_W_VW, minimised: false, maximised: false };
    const p = JSON.parse(raw);
    return {
      width_vw: Math.min(MAX_W_VW, Math.max(MIN_W_VW, Number(p.width_vw) || DEFAULT_W_VW)),
      minimised: !!p.minimised,
      maximised: !!p.maximised,
    };
  } catch { return { width_vw: DEFAULT_W_VW, minimised: false, maximised: false }; }
}
function savePrefs(p) { try { localStorage.setItem('ma-drawer-prefs', JSON.stringify(p)); } catch {} }

export default function MaSubmittalDrawer({ t, submittal, onClose, onPatched }) {
  const [tab, setTab] = useState('details');
  const [fresh, setFresh] = useState(submittal);
  const [prefs, setPrefs] = useState(loadPrefs);
  const dragStart = useRef(null);

  const setAndSave = (next) => { setPrefs(next); savePrefs(next); };

  const reload = useCallback(async () => {
    if (!submittal?.id) return;
    try {
      const doc = await fetchSln(`/api/prioris/ma/submittals/${submittal.id}`);
      setFresh(doc);
      onPatched && onPatched(doc);
    } catch { /* ignore */ }
  }, [submittal?.id, onPatched]);

  useEffect(() => { setFresh(submittal); }, [submittal]);

  // Drag-to-resize (on the left edge)
  useEffect(() => {
    const onMove = (e) => {
      if (!dragStart.current) return;
      const vw = window.innerWidth;
      const newWidthPx = Math.max(vw * (MIN_W_VW / 100), Math.min(vw * (MAX_W_VW / 100), vw - e.clientX));
      const newWidthVW = (newWidthPx / vw) * 100;
      setPrefs(p => ({ ...p, width_vw: newWidthVW, maximised: false }));
    };
    const onUp = () => {
      if (dragStart.current) {
        dragStart.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setPrefs(p => { savePrefs(p); return p; });
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const startDrag = (e) => {
    e.preventDefault();
    dragStart.current = { x: e.clientX };
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  if (!submittal) return null;

  const isConcrete = fresh?.material_family === 'concrete_mix';

  // Minimised mode: collapse to a thin bottom bar that can be restored
  if (prefs.minimised) {
    return (
      <div
        className="fixed bottom-0 right-0 z-50 flex items-center gap-2 px-3 py-2 rounded-t-lg cursor-pointer"
        style={{ width: 'min(320px, 80vw)', background: t.sidebar, borderTop: `1px solid ${t.cardBorder}`, borderLeft: `1px solid ${t.cardBorder}`, color: t.text }}
        onClick={() => setAndSave({ ...prefs, minimised: false })}
        data-testid="ma-drawer-minimised">
        <ClipboardList className="w-3.5 h-3.5" style={{ color: t.accent }} />
        <div className="flex-1 text-[11px] truncate">
          <code className="font-bold" style={{ color: t.text }}>{fresh?.internal_ref}</code>
          <span className="ml-2" style={{ color: t.textMuted }}>{fresh?.status}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setAndSave({ ...prefs, minimised: false }); }}
          className="p-1 rounded hover:bg-white/5" title="Restore" data-testid="ma-drawer-restore">
          <Maximize2 className="w-3 h-3" style={{ color: t.textMuted }} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="p-1 rounded hover:bg-white/5" title="Close" data-testid="ma-drawer-close-min">
          <X className="w-3 h-3" style={{ color: t.textMuted }} />
        </button>
      </div>
    );
  }

  const effectiveWidth = prefs.maximised ? '100vw' : `${prefs.width_vw}vw`;

  return (
    <>
      {/* backdrop (not shown when maximised so nav still visible on sides when narrower) */}
      {!prefs.maximised && (
        <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={onClose} />
      )}
      {/* drawer */}
      <aside
        className="fixed top-0 right-0 h-screen z-50 flex flex-col text-[12px]"
        style={{ width: effectiveWidth, background: t.sidebar, borderLeft: `1px solid ${t.cardBorder}`, color: t.text }}
        data-testid="ma-drawer"
      >
        {/* drag handle — left edge */}
        {!prefs.maximised && (
          <div
            onMouseDown={startDrag}
            className="absolute top-0 left-0 h-full w-1.5 cursor-ew-resize hover:bg-white/10"
            style={{ zIndex: 60 }}
            title="Drag to resize"
            data-testid="ma-drawer-resize-handle"
          />
        )}

        {/* header */}
        <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: t.cardBorder }}>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: t.accent }}>Submittal</div>
            <div className="flex items-center gap-2">
              <code className="text-xs font-bold" style={{ color: t.text }}>{fresh?.internal_ref}</code>
              <StatusBadge t={t} status={fresh?.status} />
            </div>
          </div>
          <button onClick={() => setAndSave({ ...prefs, minimised: true })}
            className="p-1.5 rounded hover:bg-white/5" title="Minimise to bottom bar"
            data-testid="ma-drawer-minimise">
            <Minus className="w-4 h-4" style={{ color: t.textMuted }} />
          </button>
          <button onClick={() => setAndSave({ ...prefs, maximised: !prefs.maximised })}
            className="p-1.5 rounded hover:bg-white/5" title={prefs.maximised ? 'Restore' : 'Maximise'}
            data-testid="ma-drawer-maximise">
            {prefs.maximised ? <Minimize2 className="w-4 h-4" style={{ color: t.textMuted }} /> : <Maximize2 className="w-4 h-4" style={{ color: t.textMuted }} />}
          </button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5" data-testid="ma-drawer-close">
            <X className="w-4 h-4" style={{ color: t.textMuted }} />
          </button>
        </div>

        {/* tabs */}
        <div className="flex border-b text-[11px]" style={{ borderColor: t.cardBorder }}>
          <TabBtn t={t} active={tab === 'details'} onClick={() => setTab('details')} icon={ClipboardList} label="Details" id="details" />
          {isConcrete && (
            <TabBtn t={t} active={tab === 'mix'} onClick={() => setTab('mix')} icon={Beaker} label="Mix Design" id="mix" />
          )}
          <TabBtn t={t} active={tab === 'comments'} onClick={() => setTab('comments')} icon={MessageSquare} label="Comments" id="comments" />
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'details'  && <DetailsTab  t={t} row={fresh} onReload={reload} />}
          {tab === 'comments' && <CommentsTab t={t} submittalId={fresh.id} />}
          {tab === 'mix' && isConcrete && <MixDesignTab t={t} row={fresh} onSaved={reload} />}
        </div>
      </aside>
    </>
  );
}

/* ─── tiny atoms ─── */
function TabBtn({ t, active, onClick, icon: Icon, label, id }) {
  return (
    <button onClick={onClick}
      className="px-4 py-2.5 flex items-center gap-1.5 font-medium border-b-2 transition-colors"
      style={{
        color: active ? t.accent : t.textMuted,
        borderColor: active ? t.accent : 'transparent',
        background: active ? t.accentBg : 'transparent',
      }}
      data-testid={`ma-drawer-tab-${id}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function StatusBadge({ t, status }) {
  const map = {
    'Draft':           { bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
    'Under Review':    { bg: 'rgba(59,130,246,0.18)',  fg: '#60a5fa' },
    'Action Required': { bg: 'rgba(245,158,11,0.18)',  fg: '#fbbf24' },
    'Approved':        { bg: 'rgba(16,185,129,0.18)',  fg: '#34d399' },
    'Rejected':        { bg: 'rgba(239,68,68,0.18)',   fg: '#f87171' },
    'Superseded':      { bg: 'rgba(168,85,247,0.15)',  fg: '#c084fc' },
    'Cancelled':       { bg: 'rgba(100,116,139,0.15)', fg: '#94a3b8' },
  };
  const c = map[status] || map.Draft;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: c.bg, color: c.fg }}>{status}</span>;
}

/* ══════════════════ DETAILS TAB ══════════════════ */
function DetailsTab({ t, row, onReload }) {
  const extRef = row?.external_ref || '';
  const looksLikeUrl = /^(https?:\/\/|www\.)/i.test(extRef);

  return (
    <div className="p-4 space-y-3" data-testid="ma-drawer-details">
      {/* summary grid */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Field t={t} label="Material type" value={row.material_family} />
        <Field t={t} label="Product / Description" value={row.product_description} />
        <Field t={t} label="Producer" value={row.producer} />
        <Field t={t} label="Asset / Zone of Use" value={row.asset_zone} />
        <Field t={t} label="Design Requirement" value={row.design_requirement} />
        <Field t={t} label="Applicable Standard" value={row.applicable_standard} />
        <Field t={t} label="Revision" value={row.revision} />
        <Field t={t} label="Date Submitted" value={row.date_submitted} />
        <Field t={t} label="Assigned Engineer" value={row.assigned_engineer} />
        <Field t={t} label="Compliance Result" value={row.compliance_result} />
      </div>

      {/* external ref — hyperlink aware */}
      <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>External Reference (Dalux / Procore / Aconex)</div>
        {extRef ? (
          looksLikeUrl ? (
            <a href={extRef.startsWith('http') ? extRef : `https://${extRef}`} target="_blank" rel="noreferrer"
              className="text-[12px] inline-flex items-center gap-1 break-all hover:underline"
              style={{ color: t.accent }} data-testid="ma-ext-link">
              {extRef} <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
            </a>
          ) : (
            <span className="text-[12px]" style={{ color: t.text }}>{extRef}</span>
          )
        ) : (
          <span className="text-[11px]" style={{ color: t.textDim }}>— paste a Dalux / Procore / Aconex URL here in the External Ref column —</span>
        )}
      </div>

      {/* location cross-ref (SLN) */}
      <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>Location</div>
        {row.sln_zone_id || row.sln_element_id ? (
          <div className="text-[12px]" style={{ color: t.text }}>
            <span style={{ color: t.accent }}>{row.sln_zone_name || '—'}</span>
            {row.sln_element_name && <span style={{ color: t.textMuted }}> → {row.sln_element_name}</span>}
          </div>
        ) : (
          <span className="text-[11px]" style={{ color: t.textDim }}>— no location set. Click the Location column in the grid to add one —</span>
        )}
      </div>

      {/* Spec Catalog compliance (from Spec Library selections) */}
      <SpecCatalogComplianceCard t={t} row={row} onReload={onReload} />

      {/* Compliance card */}
      <ComplianceCard t={t} row={row} />

      {/* Specifications (admin-defined custom fields) */}
      <SpecificationsCard t={t} row={row} onReload={onReload} />

      {/* notes */}
      {row.notes && (
        <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
          <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>Notes</div>
          <div className="text-[12px] whitespace-pre-wrap" style={{ color: t.text }}>{row.notes}</div>
        </div>
      )}

      {/* workflow actions */}
      <WorkflowStepper t={t} row={row} onReload={onReload} />

      {/* audit trail */}
      <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: t.textDim }}>Audit trail</div>
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {(row.audit_trail || []).slice().reverse().map((a, i) => (
            <div key={i} className="flex gap-2 text-[11px]">
              <Clock className="w-2.5 h-2.5 flex-shrink-0 mt-0.5" style={{ color: t.textDim }} />
              <div className="flex-1 min-w-0">
                <div style={{ color: t.textMuted }}><strong style={{ color: t.text }}>{a.action}</strong> · {a.user} · {new Date(a.ts).toLocaleString()}</div>
                {a.detail && <div className="text-[10px] break-all" style={{ color: t.textDim }}>{a.detail}</div>}
              </div>
            </div>
          ))}
          {(row.audit_trail || []).length === 0 && <div className="text-[11px]" style={{ color: t.textDim }}>No activity yet</div>}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════ WORKFLOW STEPPER (multi-stage review) ══════════════════ */
function Field({ t, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>{label}</div>
      <div className="text-[11px]" style={{ color: value ? t.text : t.textDim }}>{value || '—'}</div>
    </div>
  );
}

/* ══════════════════ COMPLIANCE CARD (auto-check vs Spec Library) ══════════════════ */
function ComplianceCard({ t, row }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchSln(`/api/prioris/ma/spec/evaluate/${row.id}`)
      .then(r => { if (live) setReport(r); })
      .catch(() => { if (live) setReport(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [row.id, row.mix_design, row.custom_fields]);

  if (loading || !report) return null;

  const { verdict, total_rules, pass, fail, missing, results, family_rules_in_library } = report;
  if (verdict === 'no_rules') {
    return (
      <div className="rounded p-2" style={{ background: t.card, border: `1px dashed ${t.cardBorder}`, color: t.textDim }}>
        <div className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Spec compliance</div>
        <div className="text-[11px] mt-0.5">No rules defined for this family yet — open the Spec Library to add requirements.</div>
      </div>
    );
  }
  if (verdict === 'no_match') {
    return (
      <div className="rounded p-2" style={{ background: t.card, border: `1px dashed ${t.cardBorder}` }}>
        <div className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Spec compliance</div>
        <div className="text-[11px] mt-0.5" style={{ color: t.textMuted }}>
          <strong style={{ color: t.text }}>{family_rules_in_library}</strong> rule{family_rules_in_library === 1 ? '' : 's'} defined for this family, but none apply to this submittal.
          Declare an <code style={{ color: t.accent }}>exposure_class</code> in the Specifications section to match exposure-scoped rules (e.g. XD3, XS2).
        </div>
      </div>
    );
  }

  const colour = verdict === 'pass' ? { bg:'rgba(16,185,129,0.12)', fg:'#34d399', label:'PASS' }
               : verdict === 'fail' ? { bg:'rgba(239,68,68,0.14)',  fg:'#f87171', label:'FAIL' }
               :                       { bg:'rgba(245,158,11,0.14)',fg:'#fbbf24', label:'PARTIAL' };

  return (
    <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${colour.fg}55` }} data-testid="ma-drawer-compliance">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Spec compliance</span>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: colour.bg, color: colour.fg }}>{colour.label}</span>
        <span className="text-[11px]" style={{ color: t.textDim }}>
          {pass}/{total_rules} rules pass{fail ? ` · ${fail} fail` : ''}{missing ? ` · ${missing} missing data` : ''}
        </span>
        <button onClick={() => setExpanded(v => !v)}
          className="ml-auto text-[10px] hover:underline" style={{ color: t.accent }}
          data-testid="ma-drawer-compliance-toggle">
          {expanded ? 'hide details' : 'details'}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 space-y-1">
          {results.map((r, i) => {
            const c = r.status === 'pass'    ? { fg:'#34d399', label:'PASS',    bg:'rgba(16,185,129,0.08)' }
                    : r.status === 'fail'    ? { fg:'#f87171', label:'FAIL',    bg:'rgba(239,68,68,0.08)'  }
                    :                          { fg:'#fbbf24', label:'MISSING', bg:'rgba(245,158,11,0.08)' };
            return (
              <div key={i} className="flex items-baseline gap-2 text-[11px] px-1.5 py-1 rounded" style={{ background: c.bg }}>
                <span className="font-semibold" style={{ color: c.fg, minWidth: 50 }}>{c.label}</span>
                <div className="flex-1 min-w-0">
                  <div style={{ color: t.text }}>{r.label}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>
                    expected {r.expected} · actual <strong style={{ color: r.status === 'pass' ? t.accent : (r.status === 'fail' ? '#f87171' : t.textMuted) }}>
                      {r.actual == null || r.actual === '' ? '—' : String(r.actual)}{r.unit ? ` ${r.unit}` : ''}
                    </strong>
                    {r.standard_ref ? <span> · <em>{r.standard_ref}</em></span> : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════ SPECIFICATIONS CARD (dynamic custom fields) ══════════════════ */
function SpecificationsCard({ t, row, onReload }) {
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null); // field name being edited
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row?.material_family) return;
    fetchSln('/api/prioris/ma/families')
      .then(d => {
        const fam = (d.families || []).find(f => f.key === row.material_family);
        setFields(fam?.custom_fields || []);
      })
      .catch(() => setFields([]));
  }, [row?.material_family]);

  if (fields.length === 0) return null;

  const cf = row.custom_fields || {};

  const startEdit = (field) => {
    setEditing(field.name);
    const v = cf[field.name];
    setValue(v == null ? '' : String(v));
  };

  const commit = async (field) => {
    let parsed = value;
    if (field.type === 'number') parsed = value === '' ? null : parseFloat(value);
    if (field.type === 'boolean') parsed = value === 'true' || value === true;
    setSaving(true);
    try {
      await fetchSln(`/api/prioris/ma/submittals/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ custom_fields: { ...cf, [field.name]: parsed } }),
      });
      await onReload();
    } catch (e) { alert(e.message); }
    setEditing(null);
    setSaving(false);
  };

  const displayValue = (field) => {
    const v = cf[field.name];
    if (v == null || v === '') return '—';
    if (field.type === 'boolean') return v ? 'Yes' : 'No';
    return `${v}${field.unit ? ` ${field.unit}` : ''}`;
  };

  return (
    <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="ma-drawer-specs">
      <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: t.textDim }}>Specifications</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {fields.map(f => (
          <div key={f.name} className="flex items-baseline gap-1" data-testid={`ma-spec-${f.name}`}>
            <div className="text-[10px]" style={{ color: t.textDim, minWidth: 85 }}>{f.label || f.name}{f.required && <span style={{ color: '#f87171' }}> *</span>}:</div>
            <div className="flex-1">
              {editing === f.name ? (
                <SpecInput t={t} field={f} value={value} setValue={setValue}
                  onBlur={() => commit(f)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commit(f); if (e.key === 'Escape') setEditing(null); }}
                  saving={saving} />
              ) : (
                <button onClick={() => startEdit(f)}
                  className="text-[11px] text-left hover:underline decoration-dotted underline-offset-2"
                  style={{ color: cf[f.name] == null || cf[f.name] === '' ? t.textDim : t.text }}>
                  {displayValue(f)}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecInput({ t, field, value, setValue, onBlur, onKeyDown, saving }) {
  const baseStyle = {
    background: t.inputBg, border: `1px solid ${t.accent}`, color: t.text,
    fontSize: 10, padding: '2px 4px', width: '100%', outline: 'none',
  };
  if (field.type === 'dropdown') {
    return (
      <select autoFocus value={value} onChange={e => setValue(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown}
        style={baseStyle} disabled={saving}>
        <option value="">—</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'boolean') {
    return (
      <select autoFocus value={String(value === true || value === 'true')} onChange={e => setValue(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown}
        style={baseStyle} disabled={saving}>
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    );
  }
  if (field.type === 'date') {
    return <input autoFocus type="date" value={value} onChange={e => setValue(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown} style={baseStyle} disabled={saving} />;
  }
  return <input autoFocus type={field.type === 'number' ? 'number' : 'text'} step={field.type === 'number' ? '0.01' : undefined}
    value={value} onChange={e => setValue(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown} style={baseStyle} disabled={saving} />;
}

const STAGES = [
  { key: 'materials_engineer', label: 'Materials Engineer', short: 'ME'  },
  { key: 'reviewer',           label: 'Reviewer',           short: 'REV' },
  { key: 'engineer_of_record', label: 'Engineer of Record', short: 'EoR' },
  { key: 'resident_engineer',  label: 'Resident Engineer',  short: 'RE'  },
  { key: 'done',               label: 'Approved',           short: '✓'   },
];

function WorkflowStepper({ t, row, onReload }) {
  const stage = row.workflow_stage || 'materials_engineer';
  const currentIdx = STAGES.findIndex(s => s.key === stage);
  const rejected = row.status === 'Rejected';

  const [busy, setBusy] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [commentText, setCommentText] = useState('');

  const act = async (action) => {
    if ((action === 'send_back' || action === 'reject') && !commentText.trim()) {
      alert('A comment is required for this action.'); return;
    }
    setBusy(true);
    try {
      await fetchSln(`/api/prioris/ma/submittals/${row.id}/stage-action`, {
        method: 'POST',
        body: JSON.stringify({ action, comment: commentText }),
      });
      setCommentText(''); setShowBack(false); setShowReject(false);
      onReload && await onReload();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div className="rounded p-2.5 space-y-2.5" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="ma-workflow-stepper">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>Approval workflow</div>
        {rejected && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(239,68,68,0.18)', color: '#f87171' }}>REJECTED</span>}
      </div>

      {/* Stepper bar */}
      <div className="flex items-center">
        {STAGES.map((s, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx && !rejected;
          const pending = i > currentIdx;
          const colour = rejected ? '#f87171' : done ? t.accent : current ? t.accent : t.textDim;
          return (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{
                    background: (done || current) ? colour + '22' : 'transparent',
                    border: `2px solid ${colour}`,
                    color: colour,
                  }}
                  title={s.label}
                >
                  {done ? '✓' : current ? '●' : i + 1}
                </div>
                <div className="text-[9px] mt-1 text-center" style={{ color: colour, maxWidth: 80, lineHeight: 1.2 }}>{s.short}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div className="flex-1 h-0.5 mx-1" style={{ background: done ? t.accent : t.cardBorder }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Current stage + actions */}
      {!rejected && stage !== 'done' && (
        <div className="pt-2 border-t" style={{ borderColor: t.cardBorder }}>
          <div className="text-[11px] mb-2" style={{ color: t.textMuted }}>
            Currently at: <strong style={{ color: t.accent }}>{STAGES[currentIdx]?.label}</strong>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stage === 'resident_engineer' ? (
              <button onClick={() => act('final_approve')} disabled={busy}
                className="px-3 py-1 rounded text-[11px] font-semibold"
                style={{ background: t.accent, color: '#000' }}
                data-testid="ma-wf-final-approve">
                ✓ Final Approve
              </button>
            ) : (
              <button onClick={() => act('forward')} disabled={busy}
                className="px-3 py-1 rounded text-[11px] font-semibold"
                style={{ background: t.accent, color: '#000' }}
                data-testid="ma-wf-forward">
                → Forward to {STAGES[currentIdx + 1]?.label}
              </button>
            )}
            {currentIdx > 0 && (
              <button onClick={() => setShowBack(v => !v)} disabled={busy}
                className="px-3 py-1 rounded text-[11px] font-medium"
                style={{ background: t.inputBg, border: `1px solid ${t.cardBorder}`, color: t.textMuted }}
                data-testid="ma-wf-send-back">
                ← Send back
              </button>
            )}
            <button onClick={() => setShowReject(v => !v)} disabled={busy}
              className="px-3 py-1 rounded text-[11px] font-medium"
              style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}
              data-testid="ma-wf-reject">
              ✕ Reject
            </button>
          </div>

          {(showBack || showReject) && (
            <div className="mt-2 pt-2 border-t" style={{ borderColor: t.cardBorder }}>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>
                {showBack ? 'Send-back reason (required)' : 'Rejection reason (required)'}
              </label>
              <textarea value={commentText} onChange={e => setCommentText(e.target.value)} rows={2}
                className="w-full mt-1 rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
                data-testid="ma-wf-comment" />
              <div className="flex gap-2 mt-2">
                <button onClick={() => act(showBack ? 'send_back' : 'reject')}
                  disabled={!commentText.trim() || busy}
                  className="px-3 py-1 rounded text-[11px] font-semibold"
                  style={{ background: showBack ? '#f59e0b' : '#ef4444', color: '#fff', opacity: commentText.trim() ? 1 : 0.5 }}
                  data-testid="ma-wf-confirm">
                  Confirm {showBack ? 'send back' : 'reject'}
                </button>
                <button onClick={() => { setShowBack(false); setShowReject(false); setCommentText(''); }}
                  className="px-3 py-1 rounded text-[11px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stage history */}
      {(row.stage_history || []).length > 0 && (
        <div className="pt-2 border-t" style={{ borderColor: t.cardBorder }}>
          <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>Stage history</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {(row.stage_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="text-[11px] flex gap-1.5" style={{ color: t.textMuted }}>
                <Clock className="w-2.5 h-2.5 flex-shrink-0 mt-0.5" style={{ color: t.textDim }} />
                <div className="flex-1 min-w-0">
                  <div><strong style={{ color: t.text }}>{h.action}</strong> — {STAGE_SHORT_LABEL[h.from_stage] || h.from_stage} → {STAGE_SHORT_LABEL[h.to_stage] || h.to_stage}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>{h.user} · {new Date(h.ts).toLocaleString()}{h.comment ? ` — ${h.comment}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const STAGE_SHORT_LABEL = {
  materials_engineer: 'ME',
  reviewer: 'REV',
  engineer_of_record: 'EoR',
  resident_engineer: 'RE',
  done: 'Approved',
};

/* ══════════════════ COMMENTS TAB ══════════════════ */
function CommentsTab({ t, submittalId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRole, setNewRole] = useState('Design Engineer');
  const [newBody, setNewBody] = useState('');
  const [newLink, setNewLink] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSln(`/api/prioris/ma/submittals/${submittalId}/comments`);
      setComments(data.comments || []);
    } catch {}
    setLoading(false);
  }, [submittalId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!newBody.trim()) return;
    setPosting(true);
    try {
      await fetchSln(`/api/prioris/ma/submittals/${submittalId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ role: newRole, body: newBody, attachment_url: newLink }),
      });
      setNewBody(''); setNewLink('');
      await load();
    } catch (e) { alert(e.message); }
    setPosting(false);
  };

  const close = async (cid) => {
    const resolution = window.prompt('Resolution note (optional):') || '';
    try {
      await fetchSln(`/api/prioris/ma/comments/${cid}/close`, { method: 'POST', body: JSON.stringify({ resolution }) });
      await load();
    } catch (e) { alert(e.message); }
  };

  const remove = async (cid) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await fetchSln(`/api/prioris/ma/comments/${cid}`, { method: 'DELETE' });
      await load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="p-4 flex flex-col h-full" data-testid="ma-drawer-comments">
      <div className="text-[11px] mb-3" style={{ color: t.textDim }}>
        Open / close the exchange between Contractor, Design Engineer and Resident Engineer.
        Paste email subject / word-file link in the attachment field if relevant.
      </div>

      {/* thread */}
      <div className="flex-1 overflow-y-auto space-y-2 mb-3">
        {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: t.accent }} />}
        {!loading && comments.length === 0 && (
          <div className="border border-dashed rounded-lg py-8 text-center" style={{ borderColor: t.cardBorder }}>
            <MessageSquare className="w-6 h-6 mx-auto mb-2" style={{ color: t.textDim }} />
            <div className="text-[12px] font-semibold mb-1" style={{ color: t.text }}>No comments yet</div>
            <div className="text-[11px]" style={{ color: t.textDim }}>Start the review conversation below.</div>
          </div>
        )}
        {comments.map(c => {
          const col = ROLE_COLOUR[c.role] || ROLE_COLOUR['Design Engineer'];
          return (
            <div key={c.id} className="rounded p-2.5" style={{
              background: t.card,
              border: `1px solid ${c.status === 'Closed' ? t.cardBorder : col.fg + '55'}`,
              opacity: c.status === 'Closed' ? 0.75 : 1,
            }} data-testid={`ma-comment-${c.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: col.bg, color: col.fg }}>
                  {c.role}
                </span>
                <span className="text-[10px]" style={{ color: t.textMuted }}>{c.author}</span>
                <span className="text-[10px]" style={{ color: t.textDim }}>{new Date(c.created_at).toLocaleString()}</span>
                {c.status === 'Closed' && (
                  <span className="ml-auto inline-flex items-center gap-0.5 text-[10px]" style={{ color: '#10b981' }}>
                    <CheckCircle2 className="w-2.5 h-2.5" /> Closed
                  </span>
                )}
              </div>
              <div className="text-[12px] whitespace-pre-wrap" style={{ color: t.text }}>{c.body}</div>
              {c.attachment_url && (
                <a href={c.attachment_url.startsWith('http') ? c.attachment_url : `https://${c.attachment_url}`}
                  target="_blank" rel="noreferrer"
                  className="text-[11px] inline-flex items-center gap-1 mt-1 hover:underline" style={{ color: t.accent }}>
                  <ExternalLink className="w-2.5 h-2.5" /> {c.attachment_url}
                </a>
              )}
              {c.resolution && (
                <div className="mt-1 pt-1 border-t text-[11px] italic" style={{ borderColor: t.cardBorder, color: t.textDim }}>
                  Resolution: {c.resolution}
                </div>
              )}
              <div className="flex gap-2 mt-1.5">
                {c.status === 'Open' && (
                  <button onClick={() => close(c.id)} className="text-[10px] inline-flex items-center gap-1 hover:opacity-80"
                    style={{ color: '#10b981' }} data-testid={`ma-comment-close-${c.id}`}>
                    <Lock className="w-2.5 h-2.5" /> Close comment
                  </button>
                )}
                <button onClick={() => remove(c.id)} className="text-[10px] inline-flex items-center gap-1 hover:opacity-80"
                  style={{ color: '#f87171' }}>
                  <Trash2 className="w-2.5 h-2.5" /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* compose */}
      <div className="rounded p-2.5" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-center gap-2 mb-2">
          <User className="w-3 h-3" style={{ color: t.textDim }} />
          <select value={newRole} onChange={e => setNewRole(e.target.value)}
            className="rounded px-2 py-1 text-[11px]"
            style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
            data-testid="ma-new-comment-role">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <textarea value={newBody} onChange={e => setNewBody(e.target.value)} rows={3}
          placeholder="Type comment — open a question, send back for info, or add a review note…"
          className="w-full rounded px-2 py-1 text-[12px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
          data-testid="ma-new-comment-body" />
        <input value={newLink} onChange={e => setNewLink(e.target.value)}
          placeholder="Attachment link (email URL, Word file, Dalux link…) — optional"
          className="w-full mt-2 rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
          data-testid="ma-new-comment-link" />
        <button onClick={post} disabled={posting || !newBody.trim()}
          className="mt-2 px-3 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
          style={{ background: t.accent, color: '#000' }}
          data-testid="ma-new-comment-submit">
          {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Post
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ MIX DESIGN TAB ══════════════════ */
const MIX_FIELDS = [
  { key: 'mix_reference',     label: 'Mix Reference',    type: 'text' },
  { key: 'producer_name',     label: 'Producer',         type: 'text' },
  { key: 'class_of_concrete', label: 'Class',            type: 'text' },
  { key: 'cement_type',       label: 'Cement Type',      type: 'text' },
  { key: 'cement_brand',      label: 'Cement Brand',     type: 'text' },
  { key: 'cement_content',    label: 'Cement (kg/m³)',   type: 'number' },
  { key: 'fly_ash_content',   label: 'Fly Ash',          type: 'number' },
  { key: 'silica_fume_content', label: 'Silica Fume',    type: 'number' },
  { key: 'ggbs_content',      label: 'GGBS',             type: 'number' },
  { key: 'coarse_aggregate',  label: 'Coarse Agg (SSD)', type: 'number' },
  { key: 'fine_aggregate',    label: 'Fine Agg (SSD)',   type: 'number' },
  { key: 'water_content',     label: 'Water',            type: 'number' },
  { key: 'admixture_type',    label: 'Admixture Type',   type: 'text' },
  { key: 'admixture_dosage',  label: 'Admixture Dosage', type: 'text' },
  { key: 'declared_fc7',      label: 'Declared fc,7 (MPa)',  type: 'number' },
  { key: 'declared_fc28',     label: 'Declared fc,28 (MPa)', type: 'number' },
  { key: 'declared_fc90',     label: 'Declared fc,90 (MPa)', type: 'number' },
  { key: 'cement_unit_cost',  label: 'Cement Cost ($/tonne)', type: 'number' },
];

function MixDesignTab({ t, row, onSaved }) {
  const [form, setForm] = useState(row.mix_design || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setForm(row.mix_design || {}); setSaved(false); }, [row.id, row.mix_design]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const totalCm = ['cement_content', 'fly_ash_content', 'silica_fume_content', 'ggbs_content']
    .reduce((a, k) => a + (parseFloat(form[k]) || 0), 0);
  const scm = ['fly_ash_content', 'silica_fume_content', 'ggbs_content']
    .reduce((a, k) => a + (parseFloat(form[k]) || 0), 0);
  const scmPct = totalCm > 0 ? ((scm / totalCm) * 100).toFixed(1) : '0.0';
  const wcRatio = totalCm > 0 ? ((parseFloat(form.water_content) || 0) / totalCm).toFixed(3) : '0.000';

  const save = async () => {
    setSaving(true);
    try {
      // Derive the fields used by the Details compliance matrix from the
      // Mix Design form so the engineer doesn't have to re-type them.
      // Mapping:
      //   cement_content (kg/m³)       → cement_content  (direct)
      //   fly_ash_content (kg/m³)      → fly_ash_percent (pct of total cm)
      //   silica_fume_content (kg/m³)  → silica_fume_percent
      //   ggbs_content (kg/m³)         → slag_percent
      //   water_content / total_cm     → wcm
      //   declared_fc28 (MPa)          → fc28_mpa
      //   cement_type / class_of_concrete / admixture_type passed through.
      const toNum = (v) => (v === '' || v === undefined || v === null) ? null : (Number.isFinite(+v) ? +v : null);
      const cm = toNum(form.cement_content);
      const fa = toNum(form.fly_ash_content);
      const sf = toNum(form.silica_fume_content);
      const sl = toNum(form.ggbs_content);
      const water = toNum(form.water_content);
      const total = [cm, fa, sf, sl].reduce((a, v) => a + (v || 0), 0);
      const pct = (v) => (total > 0 && v != null) ? +((v / total) * 100).toFixed(2) : undefined;

      const derived = {};
      if (cm != null)                derived.cement_content     = cm;
      if (total > 0 && fa != null)   derived.fly_ash_percent    = pct(fa);
      if (total > 0 && sf != null)   derived.silica_fume_percent = pct(sf);
      if (total > 0 && sl != null)   derived.slag_percent       = pct(sl);
      if (total > 0 && water != null) derived.wcm               = +((water / total).toFixed(3));
      if (toNum(form.declared_fc28) != null) derived.fc28_mpa   = toNum(form.declared_fc28);
      if (form.cement_type)          derived.cement_type        = String(form.cement_type);
      if (form.class_of_concrete)    derived.concrete_class     = String(form.class_of_concrete);
      if (form.admixture_type)       derived.accelerator_type   = String(form.admixture_type);

      const mergedCf = { ...(row.custom_fields || {}), ...derived };

      await fetchSln(`/api/prioris/ma/submittals/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ mix_design: form, custom_fields: mergedCf }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved && onSaved();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div className="p-4" data-testid="ma-drawer-mix">
      <div className="flex items-start gap-2 mb-3">
        <Beaker className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.accent }} />
        <div>
          <div className="text-xs font-bold" style={{ color: t.text }}>Full Mix Design (FHWA 1608 layout)</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>
            Enter the full mix once at approval. QC can import this data instead of re-typing.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {MIX_FIELDS.map(f => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>{f.label}</label>
            <input
              type={f.type}
              step={f.type === 'number' ? '0.1' : undefined}
              value={form[f.key] ?? ''}
              onChange={e => set(f.key, f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
              className="w-full rounded px-2 py-1 text-[12px] mt-0.5"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid={`ma-mix-${f.key}`}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-4 text-[11px] mt-3" style={{ color: t.textMuted }}>
        <span>Total binder: <strong style={{ color: t.text }}>{totalCm.toFixed(1)}</strong> kg/m³</span>
        <span>SCM: <strong style={{ color: t.text }}>{scmPct}%</strong></span>
        <span>w/cm: <strong style={{ color: t.text }}>{wcRatio}</strong></span>
      </div>

      <div className="mt-3 p-2 rounded text-[10px]"
        style={{ background: 'rgba(34,197,94,0.07)', border: `1px solid rgba(34,197,94,0.25)`, color: t.textMuted }}>
        <strong style={{ color: '#22c55e' }}>Auto-sync:</strong> Saving this form will also populate the Details tab's compliance matrix with:
        cement_content ({totalCm > 0 ? (+form.cement_content || 0).toFixed(0) : '—'} kg/m³) ·
        fly_ash_percent ({totalCm > 0 && form.fly_ash_content != null ? ((form.fly_ash_content / totalCm) * 100).toFixed(1) : '—'} %) ·
        silica_fume_percent ({totalCm > 0 && form.silica_fume_content != null ? ((form.silica_fume_content / totalCm) * 100).toFixed(1) : '—'} %) ·
        slag_percent ({totalCm > 0 && form.ggbs_content != null ? ((form.ggbs_content / totalCm) * 100).toFixed(1) : '—'} %) ·
        w/cm ({wcRatio}) ·
        fc28_mpa ({form.declared_fc28 || '—'}).
        Manual edits on the Details tab override these.
      </div>

      <button onClick={save} disabled={saving}
        className="mt-4 px-3 py-1.5 rounded text-[12px] font-semibold inline-flex items-center gap-1"
        style={{ background: t.accent, color: '#000' }}
        data-testid="ma-mix-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <CheckCircle2 className="w-3 h-3" /> : null}
        {saved ? 'Saved' : 'Save Mix Design'}
      </button>
    </div>
  );
}
